/* ══════════════════════════════════════════════════════════════════════════
   assets/asf-shop-orbit-1.js — SHOP v3「3D 軌道カルーセル」（ターンテーブル）
   ──────────────────────────────────────────────────────────────────────────
   出典: demo-v3/lab/shop-v3.html の ASF_ORBIT IIFE（L1410-2020）を移植。
         ラボ確定版＝**一段構成**（6 バリアント = 1 つの環・クローンなし・規則軌道・
         フリック速度バンド 3800px/s・厳密軸ロック・正面ドック不変・タップ 2 段階・
         奥タップ呼び寄せ・背景は単層）。
         ★ ラボで撤回済みのもの（惑星配置 / 多層パララックス / 2 段構成）は 1 行も移植して
           いない。復活禁止（経緯はラボ冒頭の★撤回メモ★が唯一の記述元）。
         ★ ラボの検証専用フック（window.__lab / ASF_BG.probeGPU 等）も移植していない。
           本ファイル末尾の公開 API は **読み取り専用**の検証フックのみで、実ナビゲーションを
           行うメソッドは 1 つも持たない。
   設計根拠: docs/SHOPV2-PORT-PLAN.md §1-a / §1-d / §I1 / §I3 / §I4 / §I5 / §R2 / §R4 / §R5。

   ★ 世代 ★ -1 は世代番号（§I5・§R5）。CDN 凍結時は -2 へ（Liquid 側の script src も同時に）。

   ══ 本番文法への適合（ラボとの差分・ここが移植の核心）══════════════════════
   (1) ★ 遷移は必ず 〈a class="card-link" href="/products/〈handle〉"〉 のクリック ★（§R2）
       本ファイルは **ページ全体を破棄する遷移 API を 1 つも書かない**（location への代入 /
       assign / replace / 新規ウィンドウのオープン / form の送信。これらは §I1 の禁止トークンと
       同じ理由でここに識別子を再掲しない）。実ナビゲーション＝ページ破棄＝永続オーディオ層ごと
       音が止まる（I1 違反）と同時に、商品オーバーレイ・ルーターの capture 段委譲を経由しなく
       なり GA の page_view も落ちる（I3 違反）。
       ラボは「タップ2 では preventDefault せず 〈a〉 の既定動作に委ねる」実装だったが、本番では
       **タップ2 のときだけ その anchor へ実 click を代理発火する** 方式に改めた（先例＝旧
       assets/asf-index.js の PC カードクリック → chip.click() 代理発火）。理由:
         ・ラボと違い、本番はカード上の click に対して「フリップ／呼び寄せ／ドラッグ後の握り潰し／
           遷移」の 4 通りの判定があり、その判定材料（中央かどうか・トゥイーン中か・直前にドラッグ
           したか・既にフリップ済みか）は **このファイルしか持っていない**。
         ・ところがルーターの捕捉は document の **capture 段**なので、下の bubble ハンドラより
           必ず先に走る。素通しに頼る設計だと「capture 側の問い合わせ関数」と「bubble 側の
           判定本体」が同じ 4 条件を二重に持つことになり、片方だけ直すと壊れる。
       → 判定を bubble ハンドラ 1 箇所に集約し、ユーザーの原クリックは **常に**
         preventDefault（＝ルーターにも 〈a〉 にも渡さない）。遷移させたいときだけ改めて
         link.click() を発火し、その 1 回だけ wantsFlipFirst() が false を返す。
         結果、capture 段のオーバーレイ化（音の連続・I1）と GA page_view（ちょうど 1 回・I3）が
         両立し、判定の二重管理も無くなる。
   (2) ★ wantsFlipFirst() は同じグローバル名・同じ契約で公開する ★
       snippets/asf-overlay-host.liquid の capture 段ハンドラが
       `window.ASF_SHOP_GRID.wantsFlipFirst(a)` を 1 行で問い合わせている（B3 で発見された
       theme-v3 固有の capture/bubble 競合への対処）。この 1 行を書き換えずに済むよう、
       グリッド実装（assets/asf-shop-grid-2.js・本移植で参照を外した死ファイル）と
       **同じ名前・同じ意味**（true = ルーターは何もせず素通し）で公開する。
       名前は window.ASF_SHOP_ORBIT にも別名で出す（新しい実体名での参照用）。
   (3) ★ SHOP 以外へ漏らさない ★ ラボは SHOP 単独ページなので無条件に document/window へ
       結線していた。本番の index は 5 ビューの単一シェルなので:
         ・ドラッグ/ホイール/クリック/フォーカスの受け口は #shop の内側（.stage-inner）に限定。
           他ビュー表示中は .view{display:none}、PDP オーバーレイ表示中は
           #shop .shop-section{visibility:hidden}（assets/asf-theme-7.css）なので、
           ヒットテストの対象にならない＝構造的に発火しない。
         ・非 passive の pointermove は **ドラッグ中だけ** window に張り、pointerup/cancel で
           必ず外す（ラボと同じ）。SHOP を離れた状態で張りっぱなしになる経路が無い。
         ・document 直結なのは keydown（←→）だけ。ここは shopActive() && !overlayOpen() で
           明示ガードする（他タブや PDP でカーソルキーが軌道を回さないように）。
       ＝ touch-action:none も非 passive リスナも他タブ/PDP へ漏れない（検証項目）。
   (4) ★ ジオメトリの実測は「幅が付いてから」★ 起動時 #shop は .view{display:none} で
       .card-wrap の offsetWidth が **0**。そのまま measure() すると軌道が潰れる
       （grid-2 で同型の不具合＝「初期色が Mint Blue になる」が実際に起きている）。
       幅が 0 の間は測らずに保留し、SHOP が最初に可視化された瞬間（MutationObserver で
       #shop.is-active / IntersectionObserver の交差）に 1 回測って配置する。
       ★ pos（リング位置）は保留・再表示のどちらでもリセットしない ★ ＝ タブ往復で
       選択中の商品が維持される（grid-2 の「初回可視化時 1 回だけセンタリング」と同じ思想）。
   (5) ★ URL・ハンドルの直書き禁止 ★（§1-d 必須作法）本ファイルは href を 1 つも組み立てない。
       商品 URL は Liquid が出した a.card-link[href] だけが持ち、ハンドルは data-handle から
       読むだけ（検証フック用）。window.ASF_ROUTES / ASF_MEDIA にも依存しない。

   ★ 不変条件 I1（音声層に触れない）★
     本ファイルは音声に関わる API・グローバル・DOM を 1 つも参照しない。禁止トークンの一覧は
     docs/SHOPV2-PORT-PLAN.md §I1 が唯一の記述元（ここに再掲すると「一覧を grep して 0 件」と
     いう検証手続き自体が自分のコメントに引っかかって成立しなくなる）。media 要素も生成しない。
   ★ 背景（assets/asf-shop-bg-2.js）とは完全に独立 ★ 本ファイルは ASF_SHOP_BG の API を
     1 つも呼ばない（ラボで一時実装した setSpin() による回転連動は撤回済み・API ごと削除済み）。
   ★ パフォーマンス ★ 毎フレーム書くのは .card-wrap の transform / filter だけ（6 要素）。
     レイアウトを誘発するプロパティ（left/top/width/height）は 1 つも触らない。rAF は
     「トゥイーン中」だけ自走し、ドラッグ中は pointermove ごとに 1 フレームだけ予約する
     （＝指が止まっていれば rAF も回らない）。静止時は完全に 0 本＝アイドル増分ゼロ。
   ══════════════════════════════════════════════════════════════════════════ */
window.asfBoot(function(){
'use strict';
if (window.__asfBootShopOrbitV1) return;                 /* シングルトン・ガード（§1-d 作法） */
window.__asfBootShopOrbitV1 = true;

var orbit = document.getElementById('shopOrbit');
if (!orbit) return;                                      /* SHOP ビューが無いテンプレートでは無害に不発 */
var host   = document.getElementById('shopStageInner');
if (!host) return;
var hintEl = document.getElementById('shopOrbitHint');
var shopView = document.getElementById('shop');

var wraps  = Array.prototype.slice.call(orbit.querySelectorAll('.card-wrap'));
if (!wraps.length) return;
var halos  = wraps.map(function(w){ return w.querySelector('.card__halo'); });
/* ★ 2026-08-02 richport ★ 承認機能1(布の慣性)/2(鏡面の床) が触る 2 種のノード。
   ・mirrors[i] … 鏡像ノード（1 枚）。apply() が opacity だけを書く。
   ・tilts[i]   … rotZ を書く層。**実体と鏡像で 2 つある**ので必ず配列で持ち、同じ
     rotate 文字列を両方へ書く（鏡像側は CSS の scaleY(-1) が符号を自動反転する）。 */
var mirrors = wraps.map(function(w){ return w.querySelector('.card__mirror'); });
var tilts   = wraps.map(function(w){
  return Array.prototype.slice.call(w.querySelectorAll('.card__tilt'));
});
var N      = wraps.length;                 /* = 6。将来バリアントが増えてもこのまま動く */
var STEP   = (Math.PI * 2) / N;
var SP_MQ  = window.matchMedia('(max-width:820px)');
var PRM    = window.matchMedia('(prefers-reduced-motion: reduce)');
function reducedMotion(){ return !!PRM.matches; }

/* ── ビュー状態のガード（本番固有・上記 (3)）────────────────────────────────
   ルーターやオーバーレイの内部実装には触れず、公開されている状態だけを読む。 */
function shopActive(){
  return !!(shopView && shopView.classList.contains('is-active'));
}
function overlayOpen(){
  try{
    return !!(window.asfProductOverlay && window.asfProductOverlay.isOpen && window.asfProductOverlay.isOpen());
  }catch(e){ return false; }
}
function interactive(){ return shopActive() && !overlayOpen(); }

/* --- 慣性/トゥイーンのパラメータ（すべてここ 1 箇所・ラボから逐語）--------------
   FLICK_V1  … 「大きなフリック」の入口(px/s)。**これ未満は速度に関わらず必ず 1 送り**。
   FLICK_VK  … そこから何 px/s 速くなるごとに 1 送り増やすか。
   MIN_DPOS  … これ未満の移動しかしていないジェスチャは「送っていない」＝起点へ戻す
   MIN_VEL   … 同上（速度側の条件。指の移動は小さいが鋭く払った場合を拾う）
   S_BASE/S_K… トゥイーンの初速係数 s の目標値 = S_BASE + S_K*|D|（s は 0..3 でのみ単調）
   T_MIN/MAX … トゥイーン時間のクランプ(ms)

   ★ FLICK_V1 / FLICK_VK の決め方（ラボ 2026-08-01・SP 390x844 dpr2 で CDP 実タッチ実測）★
   クライアント指示「横フリックがあまりに強くない限り、次の商品が正面に都度止まること。
   大きなフリックだけ回転する。」を満たす値を実測で選定した:
     しきい値候補     400px/s  1000  2700  4800   判定
       V1=2500        1        1     2     3      強(2700)が 2 送り → 指示違反
       V1=3800 ★採用  1        1     1     3      弱/中/強=1・最強のみ複数 → 指示どおり
       V1=5200        1        1     1     1      最強でも回らない → 後段が死ぬ
   しきい値の単位は px/s（指の物理速度）。steps/s だと 1 送りに要する指の移動量 pxPerStep が
   端末幅で変わる（SP 169px / PC 222px）ため、同じ手の速さでも端末で送り数が変わってしまう。 */
var FLICK_V1 = 3800, FLICK_VK = 1000;
var MIN_DPOS = 0.16, MIN_VEL = 1.2;
var S_BASE = 1.6, S_K = 0.28, S_MAX = 3.0;
/* T_MIN は 140（ラボで 190 から引き下げ済み）。既定 1 送り化で **速い離指でも D=1** に
   なったため、190ms では T ≥ 190 が効いて s = |v|·T/D が 3 を超え、S_MAX クランプで初速が
   実測 30% 削られていた（＝「速度引き継ぎ」の劣化）。140 なら 1 送りの全速度域で p'(0) = v が
   保たれる。単調性（0≤s≤3）は S_MAX クランプが従来どおり担保するので揺り戻しは起こり得ない。 */
var T_MIN = 140, T_MAX = 1100;
/* 初速ゼロ（クリック/キー/ホイール/奥のタップ由来）のときの所要時間(ms)。
   T_MAX 固定だと 1 送りに 1.1s かかって「重い」ので距離比例にする（1 送り 450ms / 6 送り 1000ms）。 */
var T_REST = 340, T_REST_K = 110;
var DRAG_SLOP = 6;      /* 軸を確定するまでの不感帯(px)。dx/dy どちらかがこれを超えた瞬間に
                           主成分の大きい方へ 1 回だけ軸を確定する（厳密軸ロック）。 */
var CLICK_SLOP = 8;     /* これを超えて動いたら click を握り潰す（ドラッグ扱い） */

/* ══ 縦ロック時の手動スクロール ════════════════════════════════════════════
   クライアント指示「横フリック中に縦全体が動いてしまう」への対処として
   .stage-inner/.orbit の touch-action を none にしたので、軸が縦と確定したジェスチャでは
   ブラウザは何もしてくれない（意図的：横確定時に競合しないための代償）。ここで JS が
   ページスクロールを代行する:
   ・ドラッグ中は onMove が window.scrollBy で **指に 1:1** 追従させる。
   ・指を離した瞬間の速度で滑り続ける簡易慣性（指数減衰）を掛け、ネイティブスクロールの
     「離した後も少し進む」感触を近似する。→ これでフッター（A.S.F. LIST 帯 → .site-footer）
     まで指 1 本で到達できる。
   ・新しいジェスチャを掴んだ瞬間（onDown）に確実に止める。
   ・reduced-motion では慣性を掛けない（指を離したら即座に静止）。 */
var scrollRAF = null, scrollVel = 0, scrollLastTS = 0;
function stopScrollMomentum(){
  if (scrollRAF !== null){ cancelAnimationFrame(scrollRAF); scrollRAF = null; }
  scrollVel = 0; scrollLastTS = 0;
}
function scrollMomentumFrame(ts){
  if (reducedMotion()){ scrollRAF = null; scrollLastTS = 0; return; }
  var dt = scrollLastTS ? Math.min((ts - scrollLastTS) / 1000, .05) : .016;
  scrollLastTS = ts;
  window.scrollBy(0, scrollVel * dt);
  scrollVel *= Math.exp(-dt / 0.22);        /* 減衰時定数 ~220ms */
  if (Math.abs(scrollVel) < 30){ scrollRAF = null; scrollLastTS = 0; return; }
  scrollRAF = requestAnimationFrame(scrollMomentumFrame);
}
function kickScrollMomentum(vPxPerSec){
  if (reducedMotion() || Math.abs(vPxPerSec) < 30) return;
  scrollVel = vPxPerSec; scrollLastTS = 0;
  if (scrollRAF === null) scrollRAF = requestAnimationFrame(scrollMomentumFrame);
}

/* --- 状態 --- */
var pos = 0;            /* 連続リング位置。整数 = そのカードが中央手前（0 = CSV 先頭） */
var geo = { tee:0, rx:0, rz:0, rise:0, tilt:0, fb:1, fc:1, fs:1, fa:1, fg:1, fh:0,
            pxPerStep:1, persp:1,
            /* 2026-08-02 richport: 機能2(鏡像の強度) / 機能1(傾きの上限) */
            rb:.13, rf:.55, tTgt:TILT_TGT, tMax:TILT_MAX };
var measured = false;   /* ★本番固有★ 幅 0（display:none）の間は false のまま保留する */
/* 各カードの実効モデル値（検証フック layout() 用に apply() が毎フレーム書き換える器）。
   要素は起動時に 1 回だけ作り、以後は使い回す（フレーム内でのアロケーション 0）。 */
var shown = (function(){ var a = [], i;
  for (i = 0; i < N; i++) a.push({ deg:0, depth:0, x:0, y:0, s:1, rot:0 });
  return a; })();
var tw = null;          /* {p0, D, T, s, t0, target} — 走行中のトゥイーン */
var raf = null;
var lastZ = new Array(N);
var lastTilt = new Array(N);   /* 直近に書いた rotate 文字列（無変化なら書かない） */
var lastRefl = new Array(N);   /* 直近に書いた鏡像 opacity 文字列（同上） */
var trace = null;       /* 検証用フレームトレース（読み取り専用フック） */

/* ══ 布の慣性（2026-08-02 クライアント承認機能1・ラボから移植）════════════════
   ★ モデル ★「支点（＝リング）が動くと吊られた布は遅れて振れ、止まったあと 1〜2 回だけ
   揺り返して静止する」＝ **速度比例の平衡点を追う 1 次元のバネ-ダンパ**。
     平衡角 θ_t = clamp(-TILT_K · v, ±TILT_TGT)     … v = リング角速度(steps/s)
     θ'' = ωn²(θ_t - θ) - 2ζωn θ'                    … ζ<1 なので減衰振動
   ・ドラッグ中は v が指の速度そのものなので「速度比例の傾き」になる。
   ・離指後は v がトゥイーンの解析速度に切り替わるが、tweenTo(target,v) は p'(0)=v を満たす
     ので切り替えの境目で平衡角が跳ばない＝**段差ゼロが構造的に保証される**。
   ・「角加速度に連動して進行方向と逆へ傾く」は、バネが平衡点を追いきれずに生じる定常誤差
     （θ_t の時間微分＝角加速度に比例）そのもの。pos の 2 階差分は極めて noisy なので
     加速度を直接使わずこの定式にしてある（同義かつ安定）。
   ★ 符号 ★ pos 増加 = θ 減少 = カードは画面左へ動く（x = rx·sinθ）。襟支点では正の角度が
     裾を左へ動かすので、pos 増加時の傾きは **負** ＝ θ_t = -TILT_K·v。
   ★ カードごとの符号 ★ 画面上の横速度は ẋ = rx·cosθ·θ̇ なので、θ=±90° は奥行きにしか
     動かず傾かず、θ=180° は手前と **逆向き** に動く。全個体を同じ向きに傾けると
     「シーン全体が傾いた」ように見えて 3D の読みが壊れる。1 本のバネの出力 swayA に
     **cosθ を掛けるだけ**で物理的に正しい分布になる（式は全個体で同一＝惑星化ではない）。
   ★ 採用値（ラボが実測で決定）★
     TILT_K  0.26 deg/(steps/s) … SP(pxPerStep=169) 換算で 1000px/s ≒ 1.5° / 2000px/s ≒ 3.1°→飽和
     TILT_TGT 2.8°（平衡点の飽和上限。|v| ≥ 10.8 steps/s ≒ SP 1820px/s で到達）
     TILT_MAX 3.6°（最終出力の硬クランプ。ζ=.45 の行き過ぎ最大 +25% ＝ 3.5° なので通常は
                    掛からないが、契約「±2〜4°上限」を構造的に担保する）
     SWAY_F  1.55Hz（周期 645ms）… 1 送りのトゥイーン（実測 0.60s）と同オーダーにすることで
                    着地時にちょうど 1 回ぶんの揺り返しが残る。
     SWAY_Z  0.45 … 行き過ぎ比 exp(-πζ/√(1-ζ²)) = 20.5% → 1 回目 20%・2 回目 4%
                    ＝目に見える揺り返しは 1〜2 回（3 回目は 0.9% で不可視）。 */
var TILT_K = 0.26, TILT_TGT = 2.8, TILT_MAX = 3.6;
var SWAY_F = 1.55, SWAY_Z = 0.45;
var SWAY_W  = 2 * Math.PI * SWAY_F;        /* ωn */
var SWAY_W2 = SWAY_W * SWAY_W;             /* ωn² */
var SWAY_2ZW = 2 * SWAY_Z * SWAY_W;        /* 2ζωn */
var SWAY_SMOOTH = 0.45;                    /* 実測角速度の指数平滑（1 フレームのぶれ対策） */
var SWAY_EPS_A = 0.02, SWAY_EPS_V = 0.15;  /* これ未満は完全静止とみなす（deg / deg/s）。
     0.02° は SP のカード（273px）の裾で 0.10px ＝ どの端末でも不可視。ここを厳しくすると
     見えない揺れのために rAF が数百 ms 余計に回るだけなので、知覚限界の 1/10 で切る。 */
/* swayA … 現在の傾き(deg・cosθ を掛ける前の代表値) ／ swayV … その角速度(deg/s)
   swayVel … リング角速度の平滑値(steps/s) ／ swayLastTS … 0 = 未初期化（次フレームで種まき） */
var swayA = 0, swayV = 0, swayVel = 0, swayLastTS = 0;

/* ══ 登場の振り付け（2026-08-02 クライアント承認機能3・ラボから移植）══════════
   6 枚が「奥から軌道へ滑り込んで整列」する初回だけのシーケンス。
   ・時間 … 1 枚あたり ENT_DUR=380ms・スタッガー ENT_STAG=45ms ×5 枚 ＝ 全体 605ms。
   ・順序 … 中央（ドック）に近い順＝|θ| 昇順。同距離（±60° など）は index 昇順で決定論的に。
   ・出発 … 最終 θ から +ENT_DTH(0.5 送り = +30°)＝右奥側へずらし、さらに ENT_Z(1.9)×Rz 奥へ
     沈める。射影 s = P/(P+depth) がそのまま効くので中央カードは s≈0.40 から原寸へ滑り込む。
     「右奥から来る」向きは、この UI の「次 = 右奥」の文法と一致させてある。
   ・イージングは **既存の 3 次エルミート g(u,s) を流用**（新しいカーブを増やさない）。
     s=ENT_S=2 ＝ 初速あり・終端速度 0 の ease-out。0≤s≤3 なので単調＝行き過ぎ無し。
   ・不変条件 … u≥1 で (1-e)=0・fade=1 が **厳密に** 成り立つので、完了後に書かれる
     transform / filter 文字列は通常ブートと 1 文字も違わない（＝画素一致の契約）。
   ★ 本番適合 ★ 「初回」の起点は DOMContentLoaded ではなく **SHOP ビューが初めて可視化された
     瞬間**（ensureLaidOut() の最初の measure() 成功）。既定の着地は CLUB なので、DOM 準備完了で
     始めるとユーザーが SHOP を開く前に演出が終わってしまう。キーも本番名前空間へ改名した。 */
var ENT_KEY = 'asf-shop-entered';
var ENT_DUR = 380, ENT_STAG = 45, ENT_S = 2;
var ENT_DTH = 0.5, ENT_Z = 1.9, ENT_FADE_U = 0.45;
var ENT_IMG_WAIT = 900;
/* entT0 … 開始時刻(performance.now)。null = 演出していない
   entHold … 画像待ちの保持状態（全カードが e=0・opacity 0 のまま静止）
   entOrder … カードごとのスタッガー順位(0..N-1)。開始時に 1 回だけ決める */
var entT0 = null, entHold = false, entBooted = false;
var entOrder = new Array(N);
for (var _e = 0; _e < N; _e++) entOrder[_e] = 0;

/* ══ ジオメトリの実測（resize / 初回可視化時のみ）══════════════════════════
   --tee は「.card-wrap の実測幅」を使う（min()/vw/vh/dvh を JS で再現しない）。
   比率（--k-rx 等）は単位なしなので getPropertyValue → parseFloat が確実に効く。
   ★ offsetWidth を使う ★ getBoundingClientRect は **変換後**のスクリーン矩形を返すので、
   既に軌道上へ散ったあとの resize では奥のカードの縮んだ幅を --tee だと誤認してしまう
   （＝リングが resize のたびに縮む）。offsetWidth は transform の影響を受けないレイアウト値。 */
function num(cs, name, dflt){
  var v = parseFloat(cs.getPropertyValue(name));
  return isFinite(v) ? v : dflt;
}
function measure(){
  var tee = wraps[0].offsetWidth || 0;
  /* ★本番固有★ #shop が .view{display:none} の間は 0。測らずに保留して false を返す。 */
  if (tee < 2) return false;
  var cs  = getComputedStyle(orbit);
  geo.tee  = tee;
  geo.rx   = tee * num(cs, '--k-rx', 1.53);
  geo.rz   = tee * num(cs, '--k-rz', 3.80);
  geo.rise = num(cs, '--orbit-rise', .12);
  geo.tilt = num(cs, '--orbit-tilt', 22);
  /* 第 2 引数は CSS 側が欠けた場合の保険。★ assets/asf-theme-7.css の #shop が宣言している値と
     必ず同じにしておくこと ★ 2026-08-01 fix1（クライアント報告「真っ白のフレンズTの白が消えて
     見えなくなっている」＝最奥に来た白 T が空気遠近で背景の柄に溶けていた不具合）で
     --fog-bright 1.16→1.06 / --fog-alpha .90→1 に変更したため、ここの既定値も同時に追随させた。
     採用根拠と実測値の唯一の記述元は assets/asf-theme-7.css の --fog-* 直上コメント。 */
  geo.fb   = num(cs, '--fog-bright',   1);
  geo.fc   = num(cs, '--fog-contrast', 1);
  geo.fs   = num(cs, '--fog-sat',      1);
  geo.fa   = num(cs, '--fog-alpha',    1);
  geo.fg   = num(cs, '--fog-gamma',    .90);
  geo.fh   = num(cs, '--fog-halo',     .55);
  /* ★ 2026-08-02 richport ★ 機能2/1 の調整口。CSS カスタムプロパティが唯一の調整口で、
     ここの第 2 引数は CSS 側が欠けた場合の保険（assets/asf-theme-9.css の #shop 宣言と
     必ず同じ値にしておくこと）。 */
  geo.rb   = num(cs, '--refl-base',    .13);
  geo.rf   = num(cs, '--refl-fade',    .55);
  geo.tTgt = num(cs, '--tilt-tgt',     TILT_TGT);
  geo.tMax = num(cs, '--tilt-max',     TILT_MAX);
  /* 1 送りに必要な指の移動量。カード実寸比で持つと端末幅に自然追随する
     （実測: SP 390px → 169px ／ PC 1280px → 222px ／ PC 1920px → 321px）。 */
  geo.pxPerStep = Math.max(72, Math.min(340, tee * 0.62));
  geo.persp = parseFloat(cs.perspective) || (tee * 5.2);
  measured = true;
  return true;
}

/* ══ 配置 ══════════════════════════════════════════════════════════════════
   (i - pos) を (-N/2, N/2] へ正規化してから 60° を掛ける＝どのカードも
   「いちばん近い等価位置」に置かれる。これだけで環は継ぎ目なく無限に回る。
       θ     = wrapDelta(i - pos) × 60°
       depth = Rz · (1 - cos θ)      … 0(手前) 〜 2Rz(最奥)
       x     = Rx · sin θ            … 右が +
       s     = P / (P + depth)       … 射影の縮尺（CSS perspective と同式）
       y     = -rise · depth         … 見下ろしの持ち上げのみ
       rotY  = tilt · sin θ          … sin テーパ（真裏でも正対に戻る）
   ★ 不変条件: θ=0 では depth=x=y=rotY=0・s=1 ＝ **選ばれた個体は必ず同一の正面ドック**に
     着地する ★ フリック・タップ・キー・ホイールのどの入力でも同じ 1 本の式を通る。 */
function wrapDelta(d){
  d = d % N;
  if (d >  N / 2) d -= N;
  if (d <= -N / 2) d += N;
  return d;
}
function apply(){
  if (!measured) return;
  var rx = geo.rx, rz = geo.rz, rise = geo.rise, tilt = geo.tilt, twoRz = 2 * rz || 1;
  var P = geo.persp;
  var center = curIndex();
  /* --- 登場の振り付け（機能3）の時間軸 ---
     entEl < 0 … 演出していない（＝通常経路。以下の分岐は 1 つも通らない）
     entHold   … 画像待ちの保持（全カード e=0・opacity 0）
     entEl >= 0 … 走行中。カード i の進捗 u = (entEl - 順位×STAG)/DUR */
  var entEl = (entT0 === null) ? -1 : (performance.now() - entT0);
  var entDone = (entEl >= 0);
  var tMaxDeg = geo.tMax;
  for (var i = 0; i < N; i++){
    /* --- 登場の進捗 e（1 = 通常状態）と fade（filter の opacity 係数）---
       ★ e===1 / fade===1 が **厳密に** 成り立つ経路を確保する ★ そうでないと演出完了後の
         transform/filter 文字列が通常ブートと 1 桁でも食い違い「画素一致」の契約が崩れる。 */
    var e = 1, fade = 1, u;
    if (entHold){ e = 0; fade = 0; }
    else if (entEl >= 0){
      u = (entEl - entOrder[i] * ENT_STAG) / ENT_DUR;
      if (u >= 1){ e = 1; fade = 1; }
      else {
        entDone = false;
        if (u <= 0){ e = 0; fade = 0; }
        else { e = hermite(u, ENT_S); fade = u < ENT_FADE_U ? (u / ENT_FADE_U) : 1; }
      }
    }

    var a0 = wrapDelta(i - pos) * STEP;   /* θ: 中央手前=0 ／ +60°=次(右奥) ／ -60°=前(左奥) */
    if (e !== 1) a0 += (1 - e) * ENT_DTH * STEP;   /* 登場: 右奥側へずらした位置から入る */
    var sa = Math.sin(a0), ca = Math.cos(a0);
    var depth = rz * (1 - ca);
    if (e !== 1) depth += (1 - e) * ENT_Z * rz;    /* 登場: さらに奥へ沈める */
    var x = rx * sa;
    var s = P / (P + depth);
    var y = -rise * depth;
    var rot = tilt * sa;

    /* --- 布の慣性（機能1）--- 1 本のバネの出力 swayA に cosθ を掛けたものがこのカードの
       傾き（＝画面上の横速度成分 rx·cosθ·θ̇ に比例）。最後に硬クランプ。
       swayA が 0（完全静止・reduced-motion）なら rotate(0.00deg) を 1 回書いて以後は
       lastTilt キャッシュで書き込み自体が起きない＝アイドル時コストゼロ。
       ★ tilts[i] は実体と鏡像の 2 つ ★ 同じ文字列を両方へ書く（鏡像は CSS の
         scaleY(-1) が符号を自動反転する＝ M·Rz(θ) = Rz(-θ)·M）。 */
    var tz = swayA * ca;
    if (tz > tMaxDeg) tz = tMaxDeg; else if (tz < -tMaxDeg) tz = -tMaxDeg;
    var tzs = tz.toFixed(2);
    if (lastTilt[i] !== tzs){
      lastTilt[i] = tzs;
      var tl = tilts[i], tstr = 'rotate(' + tzs + 'deg)';
      for (var k = 0; k < tl.length; k++) tl[k].style.transform = tstr;
    }

    wraps[i].style.transform =
      'translate3d(' + x.toFixed(2) + 'px,' + y.toFixed(2) + 'px,' + (-depth).toFixed(2) + 'px)' +
      ' rotateY(' + rot.toFixed(2) + 'deg)';
    /* 検証フック用の実効値。★毎フレーム新しいオブジェクトを作らない★（6 個 × 120fps =
       720 obj/s の GC 圧になる）。起動時に確保した同じ器を書き換えるだけ。 */
    var sh = shown[i];
    sh.deg = a0 * 180 / Math.PI; sh.depth = depth; sh.x = x; sh.y = y; sh.s = s; sh.rot = rot;

    /* 空気感（aerial perspective）。t=0 手前 / t=1 最奥。
       contrast↓ + brightness↑ が主役・opacity は補助（透けたガラスにしないため）。 */
    var t = Math.pow(Math.min(depth / twoRz, 1), geo.fg);
    wraps[i].style.filter =
      'brightness(' + (1 + (geo.fb - 1) * t).toFixed(3) + ')' +
      ' contrast('  + (1 + (geo.fc - 1) * t).toFixed(3) + ')' +
      ' saturate('  + (1 + (geo.fs - 1) * t).toFixed(3) + ')' +
      ' opacity('   + ((1 + (geo.fa - 1) * t) * fade).toFixed(3) + ')';
    if (halos[i]) halos[i].style.opacity = (1 - geo.fh * t).toFixed(3);

    /* --- 鏡面の床（機能2）の強度 --- 手前 = --refl-base ／ 最奥でその (1-fade) 倍。
       t は空気感とまったく同じ距離指標なので、鏡像・ハロー・空気感の 3 つが 1 本のカーブで
       揃う（別のフォールオフを持たない＝奥行きの読みがブレない）。鏡像ノードは .card-wrap の
       内側なので、射影の縮小と登場の fade は filter 側から自動的に掛かる。 */
    if (mirrors[i]){
      var ro = (geo.rb * (1 - geo.rf * t)).toFixed(3);
      if (lastRefl[i] !== ro){ lastRefl[i] = ro; mirrors[i].style.opacity = ro; }
    }

    /* preserve-3d の深度ソートが効いていれば z-index は無視されるが、何らかの理由で
       フラット化された環境でも前後が壊れないよう保険で書く。値が変わったときだけ書く。 */
    var z = 1000 - Math.round(depth);
    if (lastZ[i] !== z){ lastZ[i] = z; wraps[i].style.zIndex = String(z); }

    var isC = (i === center);
    if (wraps[i].classList.contains('is-center') !== isC){
      wraps[i].classList.toggle('is-center', isC);
      /* ★ 中央に来た瞬間だけ裏面を先読みする（下の primeBack のコメントが記述元）★
         is-center が **変化したフレームだけ** 通る分岐なので、毎フレームのコストはゼロ。 */
      if (isC) primeBack(i);
    }
  }
  /* 登場の終了判定。★ 最後の 1 フレームは既に e=1/fade=1 の通常経路で書かれている ★ ので、
     ここで entT0 を落としても見た目は 1px も動かない（＝完了フレームと通常ブートの出力が同一）。 */
  if (entDone) entT0 = null;
  if (trace) trace.push({ t: performance.now(), p: pos, a: swayA, tz: lastTilt[0] });
}

/* ══ 布の慣性（機能1）— リング角速度の取得 ═════════════════════════════════
   ★ pos の実フレーム微分ではなく「その入力系が持っている権威ある速度」を使う ★
     ・トゥイーン中 … currentVel()（3 次エルミートの解析微分）
     ・ドラッグ中   … drag.vel（pointermove から作った EMA・steps/s）
     ・それ以外     … 0
   pos の実微分でも理屈は同じだが、**入力イベントが 1 フレームにまとまって届く環境**
   （合成タッチ・高頻度ポインタ・スクロール復帰など）では 1 フレームぶんの pos 差がバースト
   して角速度が桁で暴れる。上の 2 つはどちらも「時刻付きの入力」から作られているので強い。
   ★ 指が止まったとき ★ pointermove が来なくなるだけなので drag.vel は古い値のまま残る。
     最後の move からの経過時間で指数的に 0 へ落として「指を止めたら布も静まる」を作る
     （時定数 90ms ＝ 1 フレーム 2〜3 個ぶん）。 */
function ringVelocity(){
  if (tw) return currentVel();
  if (drag && drag.axis === 'x'){
    var age = performance.now() - drag.moveNow;
    if (age <= 0) return drag.vel;
    return drag.vel * Math.exp(-age / 90);
  }
  return 0;
}

/* ══ 布の慣性（機能1）— バネ-ダンパの積分 ═══════════════════════════════════
   frame() から毎フレーム 1 回だけ呼ばれる（apply() の直前＝同じフレームの pos で微分を取る）。
   ・dt は積分の安定のため 50ms で頭打ちにし、20ms を超えるフレームは 2 分割して半陰的オイラーで
     積分する（タブ復帰などの巨大 dt でバネが発散しない）。
   ・完全静止（|θ|・|θ'|・|v| がすべて閾値未満）になったら 3 変数を **厳密に 0** へ落とす。
     これが frame() の自走停止条件（swayActive）になり、アイドル時 rAF ゼロの既存契約を保つ。 */
function swayStep(ts){
  if (reducedMotion()){ swayA = 0; swayV = 0; swayVel = 0; swayLastTS = 0; return; }
  if (!swayLastTS){ swayLastTS = ts; return; }        /* 種まき（1 フレーム） */
  var real = (ts - swayLastTS) / 1000;
  swayLastTS = ts;
  if (real <= 0) return;
  swayVel += (ringVelocity() - swayVel) * SWAY_SMOOTH;

  var tgt = -TILT_K * swayVel;
  if (tgt > geo.tTgt) tgt = geo.tTgt; else if (tgt < -geo.tTgt) tgt = -geo.tTgt;

  var dt = real > 0.05 ? 0.05 : real;
  var n = dt > 0.02 ? 2 : 1, h = dt / n, j;
  for (j = 0; j < n; j++){
    swayV += (SWAY_W2 * (tgt - swayA) - SWAY_2ZW * swayV) * h;
    swayA += swayV * h;
  }
  if (Math.abs(swayA) < SWAY_EPS_A && Math.abs(swayV) < SWAY_EPS_V &&
      Math.abs(swayVel) < SWAY_EPS_V){ swayA = 0; swayV = 0; swayVel = 0; }
}
function swayActive(){ return swayA !== 0 || swayV !== 0 || swayVel !== 0; }

/* ══ 登場の振り付け（機能3）の駆動 ═══════════════════════════════════════════ */
/* スタッガー順位 = ドックからのリング距離の近い順（|θ| 昇順）。同距離（±60° など）は index の
   小さい方を先にして決定論的にする（＝毎回まったく同じ振り付けになる）。 */
function buildEntranceOrder(){
  var idx = [], i;
  for (i = 0; i < N; i++) idx.push(i);
  idx.sort(function(a, b){
    var da = Math.abs(wrapDelta(a - pos)), db = Math.abs(wrapDelta(b - pos));
    return da === db ? (a - b) : (da - db);
  });
  for (i = 0; i < idx.length; i++) entOrder[idx[i]] = i;
}
function startEntrance(){
  if (!entHold) return;                 /* 既に打ち切られていたら何もしない */
  entHold = false;
  buildEntranceOrder();
  entT0 = performance.now();
  if (raf === null) raf = requestAnimationFrame(frame);
}
/* 演出中の入力は「無視」ではなく「打ち切って即整列」（上品な割り込み）。呼び出し元
   （onDown / wheel / click / keydown / edge / focusin / ホバー呼び寄せ）はこの後、通常どおり
   自分の仕事を続ける＝ユーザーの 1 アクションが捨てられない。 */
function abortEntrance(){
  if (!entHold && entT0 === null) return false;
  entHold = false; entT0 = null;
  apply();                              /* 即座に通常状態（＝通常ブートと同一の出力） */
  return true;
}
/* front 画像が **描画可能になるまで** 開始を待つ（白抜けのまま滑り込ませない）。
   decode() があれば「デコード完了」まで待てる（complete だけだと decoding="async" のデコード
   途中で 1 フレーム白く出ることがある）。ENT_IMG_WAIT で必ず頭打ち＝画像が来なくても必ず出る。
   ★ querySelectorAll は実体 + 鏡像の 2 枚を拾うが URL は同一なので待ちは実質 1 枚ぶん。
   ★ 本番では Liquid 側で front が loading="eager" になっている（2026-08-01 fix2）ため、
     この待ちはほぼ即時に解ける（＝演出開始が遅延しない）。 */
function whenFrontsReady(cb){
  var imgs = [], i, j, list;
  for (i = 0; i < N; i++){
    list = wraps[i].querySelectorAll('.tee-img--front');
    for (j = 0; j < list.length; j++) imgs.push(list[j]);
  }
  var left = imgs.length, done = false;
  function fire(){ if (done) return; done = true; clearTimeout(to); cb(); }
  function dec(){ if (--left <= 0) fire(); }
  var to = setTimeout(fire, ENT_IMG_WAIT);
  if (!left){ fire(); return; }
  for (i = 0; i < imgs.length; i++){
    if (imgs[i].decode){ imgs[i].decode().then(dec, dec); }
    else if (imgs[i].complete){ dec(); }
    else {
      imgs[i].addEventListener('load', dec, { once:true });
      imgs[i].addEventListener('error', dec, { once:true });
    }
  }
}
/* ── 登場のゲート ────────────────────────────────────────────────────────────
   ① reduced-motion → 演出なし・即時表示
   ② sessionStorage に印がある（＝同一セッション内の再訪）→ 演出なし
   ③ それ以外（＝このセッションの初回）→ 保持状態で描いてから front の decode 完了を待って開始
   印はどの分岐でも **必ず** 立てる（＝「初回」はセッション内で 1 度きり）。
   sessionStorage は Private Browsing 等で例外を投げ得るので必ず try で包む
   （投げた場合は「演出しない」側に倒す＝壊れた見え方より無演出のほうが安全）。
   ★ 本番適合 ★ 呼び出し元は ensureLaidOut()（＝SHOP が初めて可視化された瞬間）ただ 1 箇所。 */
function bootEntrance(){
  var first = false;
  try {
    first = !reducedMotion() && !window.sessionStorage.getItem(ENT_KEY);
    window.sessionStorage.setItem(ENT_KEY, '1');
  } catch (err){ first = false; }
  if (!first){ apply(); return; }
  entHold = true;
  apply();                          /* 保持状態（奥・不可視）を 1 枚描く＝CLS 0 のまま */
  whenFrontsReady(startEntrance);
}

/* ══ 画像の先読み ══════════════════════════════════════════════════════════
   ★★ 2026-08-01 fix2 — 「初回訪問時、最奥（θ=180°）の Friends White が映らない」の根治 ★★
   真因と実測値の唯一の記述元は snippets/asf-tab-shop.liquid の 〈img〉 直上コメント。要約すると
   「front/back の loading="lazy" を WebKit（iPhone Safari）が θ=180° の個体について
     『ビューポート外』と判定し、画像要求を 1 度も発行しない」。
   一次修正は Liquid 側で front を loading="eager" に戻したこと。ここはその **保険と最適化** の 2 役:

     ① primeFronts() … SHOP が実際に可視化された瞬間に、front 6 枚の fetchPriority を high へ
        引き上げる。着地ビューは CLUB なので Liquid 側は fetchpriority="low"（＝要求は必ず出すが
        CLUB の帯域は奪わない）にしてあり、SHOP を開いた時点で初めて「主役」に昇格させる。
        同時に loading が lazy のままの 〈img〉 があれば eager へ書き換える
        ＝ 将来 Liquid 側に lazy が再混入しても、この 1 行が最奥カードの欠落を止める。
     ② primeBack(i) … カードが中央（θ=0）に来た瞬間に、その 1 枚の裏面だけを eager へ。
        裏面はフリップでしか見えず、フリップ可能なのは中央の 1 枚だけなので、これで
        「初フリップで裏が白抜け」を防ぎつつ、先読みするバイト数は常に 1 枚ぶんに留まる。

   ★ 冪等・非破壊 ★ どちらも属性を 1 回書き換えるだけで、DOM の増減も src の書き換えも行わない
     （＝ fragment 契約・GA・音声層のいずれにも触れない）。 */
function primeImg(el, prio){
  if (!el) return;
  if (el.getAttribute('loading') === 'lazy') el.setAttribute('loading', 'eager');
  /* fetchPriority 非対応エンジン（Safari 17.1 以前など）では単に無視される＝無害 */
  if (prio && 'fetchPriority' in el) el.fetchPriority = prio;
}
/* ★ 2026-08-02 richport の注意 ★ 鏡像の導入で .tee-img--front / --back は 1 カードにつき
   **2 枚**（実体 + 鏡像・同一 URL）になった。querySelector は DOM 順で先に来る **鏡像側**を
   掴んでしまい実体が素通しになるので、必ず querySelectorAll で全件に掛ける。 */
function primeAll(w, sel, prio){
  if (!w) return;
  var list = w.querySelectorAll(sel);
  for (var k = 0; k < list.length; k++) primeImg(list[k], prio);
}
var frontsPrimed = false;
function primeFronts(){
  if (frontsPrimed) return;
  frontsPrimed = true;
  for (var i = 0; i < N; i++) primeAll(wraps[i], '.tee-img--front', 'high');
}
function primeBack(i){
  primeAll(wraps[i], '.tee-img--back', 'low');
}

function curIndex(){ return ((Math.round(pos) % N) + N) % N; }

/* ══ トゥイーン ════════════════════════════════════════════════════════════
   3 次エルミート g(u) = (3u²-2u³) + s(u³-2u²+u)。
   g'(u) = (1-u)[u(6-3s)+s] なので 0 ≤ s ≤ 3 の範囲では g' ≥ 0 ＝ **常に単調**。
   → 行き過ぎ（オーバーシュート）も揺り戻しもバウンドも数学的に起こり得ない。
   s = v0*T/D なので p'(0) = v0 ＝ 離した瞬間の速度がそのまま初速になる。 */
function hermite(u, s){
  var u2 = u * u, u3 = u2 * u;
  return (3 * u2 - 2 * u3) + s * (u3 - 2 * u2 + u);
}
function hermiteD(u, s){ return (1 - u) * (u * (6 - 3 * s) + s); }

/* いま走っているトゥイーンの解析的な速度（steps/s）。連続フリック・ホイール連打・
   奥のタップで割り込んだとき、この値を次のトゥイーンの初速へ引き継ぐ
   ＝「一度止まってから再加速する」段付きが出ない。 */
function currentVel(){
  if (!tw) return 0;
  var u = (performance.now() - tw.t0) / tw.T;
  if (u >= 1 || u < 0) return 0;
  return tw.D * hermiteD(u, tw.s) / (tw.T / 1000);
}

function tweenTo(target, v0){
  var D = target - pos;
  if (Math.abs(D) < 1e-4){ tw = null; settle(target); return; }
  if (reducedMotion()){ tw = null; pos = target; settle(target); apply(); return; }

  var v = v0 || 0;
  if (v * D <= 0) v = 0;                       /* 逆向き/無速度は引き継がない（逆走の芽を潰す） */
  var absD = Math.abs(D), absV = Math.abs(v);
  var sWant = Math.min(S_BASE + S_K * absD, S_MAX);
  var T = absV > 0.001 ? (1000 * sWant * absD / absV) : (T_REST + T_REST_K * absD);
  T = Math.max(T_MIN, Math.min(T_MAX, T));
  var s = absV > 0.001 ? (absV * (T / 1000) / absD) : 0;
  s = Math.max(0, Math.min(S_MAX, s));         /* ★ここが「バウンド/揺り戻しゼロ」の担保★ */

  tw = { p0: pos, D: D, T: T, s: s, t0: performance.now(), target: target };
  if (raf === null) raf = requestAnimationFrame(frame);
}

/* 着地。★ 毎回 mod N へ畳まない ★ 畳むと pos が 5.999 → 0 と跳び、フレームトレースの
   「逆走ゼロ」判定に偽陽性のノイズが出る。浮動小数の精度は |pos|=6000 でも 1e-12 なので、
   十分大きくなったときだけ畳む。 */
function settle(target){
  pos = Math.round(target);
  if (pos > 6000 || pos < -6000) pos = ((pos % N) + N) % N;
}

function frame(ts){
  raf = null;
  if (tw){
    var u = (ts - tw.t0) / tw.T;
    if (u >= 1){ pos = tw.target; settle(tw.target); tw = null; }
    /* ★ u を 0 でクランプする ★ rAF のタイムスタンプ ts は「そのフレームの開始時刻」なので、
       pointerup ハンドラ内で取った tw.t0（performance.now()）より **前** になることが実際に
       ある（実測 1〜3ms）。u が負のまま hermite に入ると g(u)<0 となり、離した瞬間に
       0.001 step だけ戻る＝微小な逆走がフレームトレースに現れる。目には見えない量だが、
       「揺り戻しゼロ」を数値で保証する契約なので構造的に潰す。 */
    else pos = tw.p0 + tw.D * hermite(u > 0 ? u : 0, tw.s);
  }
  swayStep(ts);            /* ★ apply() の直前 ★ 同じフレームの pos で微分を取るため */
  apply();
  clearFlipOnSpin();
  /* ★ 自走の継続条件 ★ トゥイーン中／登場演出中／布がまだ揺れている、のいずれか。
     どれも終われば rAF は完全に止まる（＝アイドル時の追加 rAF ゼロという既存契約を維持）。
     ★ swayLastTS はここで落とさない ★ ドラッグ中は「指が動いたときだけ 1 フレーム予約」なので、
     ここで種を落とすと毎フレーム「種まき → return」を繰り返して **ドラッグ中の傾きが一生 0 のまま**
     になる（ラボの実測で判明）。落とさずに持ち越せば、アイドルを挟んだ次のジェスチャでは dt が
     巨大 → 角速度 ≒ 0 となって 1 フレームだけ不感になるだけで、2 フレーム目から正しく積分が
     始まる（自己修復する）。 */
  if (tw || entT0 !== null || swayActive()) raf = requestAnimationFrame(frame);
}
/* ドラッグ中は「動いたときだけ 1 フレーム予約」。指が止まっていれば rAF は回らない。 */
function schedule(){ if (raf === null) raf = requestAnimationFrame(frame); }

/* ══ 送り（キー / エッジクリック / ホイール / 奥のタップ）══════════════════ */
function go(steps){
  var base = tw ? tw.target : Math.round(pos);
  var t = base + steps;
  if (t === base) return;
  tweenTo(t, currentVel());
}
function goToIndex(i){
  var d = wrapDelta(i - Math.round(tw ? tw.target : pos));
  if (d === 0) return;
  go(d);
}

/* ══ ドラッグ / フリック ═══════════════════════════════════════════════════ */
var drag = null;
var clickGuard = 0;
var lastFlick = null;   /* 検証用: 直近のフリックの {vpx, n, steps, dir} */

/* 離指速度 → 送り数（steps）。**既定は 1**、FLICK_V1 を超えたぶんだけ段階的に増える。
   引数 absV は steps/s（内部単位）。しきい値は px/s なので pxPerStep を掛けて比較する。
   単調性: vpx について非減少・上限 N（1 周）。 */
function flickSteps(absV){
  var vpx = absV * geo.pxPerStep;
  if (vpx < FLICK_V1) return 1;
  return Math.min(N, 2 + Math.floor((vpx - FLICK_V1) / FLICK_VK));
}

function onDown(e){
  if (e.button !== undefined && e.button !== 0) return;
  if (e.target.closest && e.target.closest('.edge-nav')) return;
  if (!measured && !measure()) return;      /* ★本番固有★ 幅がまだ付いていないなら何もしない */
  var v = currentVel();
  if (tw){ tw = null; }             /* 走行中を掴んだら、その位置で受け止める */
  stopScrollMomentum();             /* 縦の慣性が滑っている最中に掴んだら即座に止める */
  hoverCancel();                    /* ★pcfix2★ 掴んだらホバー呼び寄せの意図判定は破棄する */
  abortEntrance();                  /* ★richport★ 演出中に触られたら即整列してから通常のドラッグへ */
  drag = { id: e.pointerId, x0: e.clientX, y0: e.clientY,
           px: e.clientX, pt: (e.timeStamp || performance.now()),
           startPos: pos, startIdx: Math.round(pos),
           vel: v, axis: null, moved: 0,
           /* ★richport★ 布の慣性の「指が止まった」判定用。必ず performance.now() で持つ
              （e.timeStamp は環境や合成入力で基準時刻が別系統になることがある）。 */
           moveNow: performance.now(),
           lastY: e.clientY, ptY: (e.timeStamp || performance.now()), velY: 0 };
  /* setPointerCapture は使わない: capture すると pointerup の配送先が .orbit へすり替わり、
     その結果 click のターゲットまで .orbit になってカードのタップ（フリップ / 遷移 /
     呼び寄せ）が壊れる。window で拾えば targeting は素のまま。
     ★ pointermove だけ非 passive にする ★ 軸が横と確定した後、毎 touchmove で
     preventDefault するにはリスナー自体が cancelable を握れないといけない
     （passive:true だと呼んでも黙って無視される）。pointerup/cancel はデフォルト動作を
     止める必要がないので passive のまま。★ ここで張ったリスナは endDrag() が必ず外す
     （＝SHOP を離れた状態で張りっぱなしになる経路が存在しない）。 */
  window.addEventListener('pointermove', onMove, { passive:false });
  window.addEventListener('pointerup', onUp, { passive:true });
  window.addEventListener('pointercancel', onCancel, { passive:true });
}
/* ══ onMove — 軸ロック本体 ══════════════════════════════════════════════════
   クライアント指示「横フリック中に縦全体が動いてしまう」への根治。原因は
   touch-action:pan-y が持つ構造的な競合だった: pan-y は「縦パンはブラウザのものにしてよい」
   という宣言なので、ブラウザは JS の DRAG_SLOP（6px）よりずっと小さい・非公開の自前閾値で
   「これは縦パンだ」と先にコミットしてしまうことがある。一度コミットした touchmove は以後
   cancelable が false になり、JS がどれだけ preventDefault しても止められない。
   対処は 2 段構え:
   ① CSS 側で touch-action:none にし、ブラウザの自動パン判定そのものを消す
      （assets/asf-theme-7.css の .stage-inner/.orbit）。軸の確定は 100% ここ（JS）が行う。
   ② 確定は DRAG_SLOP を初めて超えた瞬間に **主成分の大きい方へ 1 回だけ**（斜めの曖昧
      ジェスチャも同じ規則で解決する）。以後そのジェスチャが終わるまでブレない。
        ・横（'x'）確定 → 毎 touchmove で e.preventDefault()（cancelable な限り）。
        ・縦（'y'）確定 → JS が window.scrollBy で 1:1 追従してページスクロールを代行する。
          横回転のロジック（pos の更新・schedule()）へは一切入らない。 */
function onMove(e){
  if (!drag || e.pointerId !== drag.id) return;
  var x = e.clientX, y = e.clientY;
  var dx = x - drag.x0, dy = y - drag.y0;
  drag.moved = Math.max(drag.moved, Math.sqrt(dx * dx + dy * dy));

  if (drag.axis === null){
    if (Math.abs(dx) < DRAG_SLOP && Math.abs(dy) < DRAG_SLOP) return;
    /* 主成分の大きい方に寄せる（横優位＝dx が同値以上なら横をロック）。 */
    drag.axis = Math.abs(dx) >= Math.abs(dy) ? 'x' : 'y';
    if (drag.axis === 'y'){
      drag.lastY = y; drag.ptY = (e.timeStamp || performance.now()); drag.velY = 0;
    } else {
      host.classList.add('is-dragging');
    }
  }

  if (drag.axis === 'y'){
    /* touch-action:none にしたぶん、ここで JS がページスクロールを代行する（★指に 1:1 追従★）。
       preventDefault は呼ばない＝ページの他のジェスチャ処理を邪魔しない。 */
    var nowY = e.timeStamp || performance.now();
    var dtY  = Math.max(nowY - drag.ptY, 1);
    var dyStep = y - drag.lastY;
    var instY = dyStep / dtY * 1000;                 /* px/s（指の生速度） */
    drag.velY = drag.velY === 0 ? instY : (drag.velY * 0.65 + instY * 0.35);
    drag.ptY = nowY; drag.lastY = y;
    if (dyStep) window.scrollBy(0, -dyStep);
    return;
  }

  /* ★ 横確定後は毎 touchmove で必ず preventDefault ★ ジェスチャが終わるまで縦スクロールの
     漏れを構造的に 0px にする（cancelable な限り＝ passive:false + touch-action:none の組で
     常に true のはず。実測で検証する）。 */
  if (e.cancelable) e.preventDefault();

  var now = e.timeStamp || performance.now();
  var dt  = Math.max(now - drag.pt, 1);
  var inst = -(x - drag.px) / geo.pxPerStep / dt * 1000;      /* steps/s */
  /* 指数平滑（直近を 0.35 で重み付け）。生の 1 サンプルだと最後の 1 フレームのぶれが
     そのまま初速に化けるため。 */
  drag.vel = drag.vel === 0 ? inst : (drag.vel * 0.65 + inst * 0.35);
  drag.px = x; drag.pt = now;
  drag.moveNow = performance.now();          /* ★richport★ 布の慣性の「指が止まった」判定用 */

  pos = drag.startPos - dx / geo.pxPerStep;    /* ★指に 1:1 追従★ */
  schedule();
}
function onUp(e){
  if (!drag || e.pointerId !== drag.id) return;
  var d = drag, moved = d.moved, axis = d.axis;
  endDrag();
  if (moved > CLICK_SLOP) clickGuard = performance.now();
  if (axis === 'y'){
    /* 離した瞬間の縦速度で簡易慣性へ引き継ぐ（符号は onMove の scrollBy と同じ規約）。
       横回転はここでは一切駆動しない。 */
    kickScrollMomentum(-d.velY);
    return;
  }
  if (axis !== 'x') return;

  /* --- 慣性 → 正面スナップ（既定 1 送り）------------------------------------
     ① 送っていない（微動/タップ）→ 起点へ戻す
     ② 速度バンドで送り数 n を決める（既定 1・大きなフリックだけ 2 以上）
     ③ 着地点 = 進行方向の「次の detent」+ dir*(n-1)
        ★「次の detent」を基準にするのが要点 ★ 起点 startIdx 基準にすると、指で 2.5 送りぶん
          運んでから軽く離した場合に target = startIdx+1 ＝ **後戻り**になる。
     ④ 起点から最低 1 送り（＝必ずどこかへ 1 つ進む）
     ⑤ 起点から最大 N 送り＝慣性は 1 周でクランプ
     ⑥ 最後に「次の detent 以上」を再適用＝**逆走を構造的に禁止**
        （⑤ の後に置くのが要点。⑤ が最後だと、1 ジェスチャで N 送り超を指で運べる広い画面
          （PC 1920 で 6*321=1926px）では maxT が pos より手前に来て逆走し得た。） */
  var dpos = pos - d.startPos;
  var v = d.vel;
  if (Math.abs(dpos) < MIN_DPOS && Math.abs(v) < MIN_VEL){ tweenTo(d.startIdx, 0); return; }
  var dir = Math.abs(v) >= 0.6 ? (v > 0 ? 1 : -1) : (dpos > 0 ? 1 : -1);

  var n = flickSteps(Math.abs(v));
  var nextDetent = dir > 0 ? Math.floor(pos + 1e-6) + 1 : Math.ceil(pos - 1e-6) - 1;
  var target = nextDetent + dir * (n - 1);
  var minT = d.startIdx + dir;
  if (dir > 0 ? target < minT : target > minT) target = minT;
  var maxT = d.startIdx + dir * N;
  if (dir > 0 ? target > maxT : target < maxT) target = maxT;
  if (dir > 0 ? target < nextDetent : target > nextDetent) target = nextDetent;

  lastFlick = { vpx: Math.round(Math.abs(v) * geo.pxPerStep), n: n,
                steps: Math.abs(target - d.startIdx), dir: dir };
  tweenTo(target, v);
}
function onCancel(){ if (drag) endDrag(); }
function endDrag(){
  drag = null;
  host.classList.remove('is-dragging');
  window.removeEventListener('pointermove', onMove);
  window.removeEventListener('pointerup', onUp);
  window.removeEventListener('pointercancel', onCancel);
}
host.addEventListener('pointerdown', onDown, { passive:true });

/* ══ ホイール（PC）══════════════════════════════════════════════════════════
   縦横どちらの成分でも「主軸のほう」を採る。**1 ジェスチャ 1 送り固定**。
   クライアント指示「次の商品が正面に都度止まること」は入力手段を問わない要求なので、
   フリック（既定 1 送り）とホイールで挙動が食い違わないように 1 送りへ揃えた。
   ★ 受け口（.stage-inner）の矩形の上でだけ preventDefault する ★ ステージの上下に残した帯
   （1280x800 で上下 141px ずつ）・A.S.F. LIST 帯・フッターの上ではページが素直にスクロール
   してフッターへ届く＝ホイールで閉じ込めない。スクロールバー操作も当然そのまま効く。 */
/* ★★ 2026-08-02 pcfix1 — クライアント報告「PC版がトラックパッドのフリックで回転し過ぎます。
   スマホ同様に次の T シャツでスムーズに止まるように」の根治 ★★（本コメントが唯一の記述元）

   ── 旧実装が多段送りになっていた理由（実測で特定）──
     旧コードは「220ms 途切れたら別ジェスチャ」＋「送信後 170ms のクールダウン(wLock)」だった。
     ところがトラックパッドのフリックは **物理フェーズ（指が触れている ~100ms）に続いて
     OS が慣性尾（momentum）を 0.8〜2.0 秒ぶん送り続ける**。この尾はイベント間隔が 10〜16ms
     なので 220ms の切れ目は一度も訪れず、しかも旧コードは wLock 中も wAcc に加算し続けていた
     （`wAcc += d` が `if (now < wLock) return` より **前** にあった）ため、
     クールダウンが明けた瞬間には既に閾値を超えており、170ms ごとに送りが出続けていた。
     実測（合成した macOS 相当の慣性列・PC1280・各10回）:
       通常フリック 7〜8 送り ／ 強いフリック 12〜15 送り ／ 弱いフリック 3〜4 送り
     ＝ クライアントの「回転し過ぎ」と完全に一致。

   ── 採用したロジック「1 連続ストリーム = 1 送り」──
     ① ストリームが W_IDLE_END(110ms) 以上途切れたら、そこが必ずジェスチャの境界。
        → 離散ノッチのマウスホイールは 1 ノッチ = 1 ジェスチャ = 1 送り（従来どおり）。
          ノッチが 110ms 未満で連続する「1 ノッチ群」は 1 ジェスチャ = 1 送りに畳まれる。
     ② 送りを出したら **武装解除**（wArmed=false）し、以後そのストリームのデルタは
        累積しない・送りも出さない＝慣性尾は何秒続いても丸ごと飲み込む。
        ★ ここが旧実装との決定的な差 ★ 飲み込む間も preventDefault は続けるので、
          慣性尾がページスクロールへ漏れることはない。
     ③ ただし「尾の途中で意図的に 2 本目を弾いた」場合は受理しなければならない
        （過剰なクールダウンで操作が死ぬのを防ぐ）。慣性尾は **単調減衰** するという性質を使い、
        武装解除中の最小デルタ wTailMin を追跡して
            ad > wTailMin * W_RISE_K  かつ  ad > W_RISE_MIN
        を満たす「明らかな再加速」だけを新しいジェスチャとして再武装する。
        wTailMin は常に ad 以下に更新されるので、単調減衰しているあいだは
        ad == wTailMin となり ad > ad*2.2 は原理的に成立しない＝尾では絶対に誤発火しない。
     ④ ★ 減衰ゲート（wDecay）★ ③ には落とし穴があり、これで一度実装を落としている。
        送りが出るのは累積が 34 を超えた時点＝**まだ物理フェーズでデルタが増加している最中**
        なので、直後の上昇デルタを wTailMin に取り込むと基準値が不当に小さく固定され、
        慣性尾のピーク（尾は物理フェーズ末より一段高い値から始まる）が
        「再加速」と誤判定される。ブラウザ実測列で t=+326ms・ad=62.1 に対し
        wTailMin=26.0（閾値 57.2）となり 2 送り目が出ていた。
        → **一度でもデルタが減少に転じてから**（ad <= 直前の ad）初めて尾とみなし、
          そこから wTailMin の記録と再加速判定を始める。これで基準値は必ず「尾のピーク以降の
          最小値」になり、単調減衰中の誤発火が原理的に消える。
        W_MIN_GAP(300ms) は二重の保険（物理フェーズは実測 ~100ms）。
   ★ 値の根拠 ★ 合成慣性列（通常/強/弱フリック・低速パン・ノッチ）＋ ブラウザから採取した
     実イベント列 ＋ 間隔にランダムジッタを乗せた 200 回試行、のすべてで
     「1 ジェスチャ = 1 送り」かつ「連続2フリック(300/420/600/900/1400ms)= 2 送り」が
     成立する組み合わせ。 */
var W_FIRE     = 34;    /* 送りを出すのに要る累積デルタ（旧実装から据置） */
var W_IDLE_END = 110;   /* この時間 wheel が途切れたらジェスチャ終了とみなす(ms) */
var W_MIN_GAP  = 300;   /* 送り直後、再加速判定を始めない時間(ms)＝物理フェーズを覆う保険 */
var W_RISE_K   = 2.2;   /* 慣性尾の最小デルタの何倍で「新しい押し込み」とみなすか */
var W_RISE_MIN = 12;    /* 上と併用する絶対下限(px)。尾の終盤の微小デルタを弾く */

var wAcc = 0, wLast = 0, wArmed = true, wFireT = 0;
var wTailMin = Infinity, wPrevAd = 0, wDecay = false;
host.addEventListener('wheel', function(e){
  if (SP_MQ.matches) return;
  if (!interactive()) return;
  if (!measured && !measure()) return;
  var now = performance.now();
  var gap = now - wLast;
  wLast = now;
  var d = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
  if (e.deltaMode === 1) d *= 16;       /* line mode */
  else if (e.deltaMode === 2) d *= 400; /* page mode */
  if (!d) return;
  e.preventDefault();
  abortEntrance();                      /* ★richport★ 演出中でも入力を捨てない（即整列して続行） */
  var ad = Math.abs(d);

  /* ① ストリームが途切れた＝確実にジェスチャの境界 */
  if (gap > W_IDLE_END){
    wArmed = true; wAcc = 0; wTailMin = Infinity; wDecay = false; wPrevAd = 0;
  }

  /* ②③④ 武装解除中は慣性尾を飲み込む。明らかな再加速だけ 2 本目として受理する。 */
  if (!wArmed){
    if (ad <= wPrevAd) wDecay = true;      /* ④ 一度でも減ったら以後は「尾」とみなす */
    wPrevAd = ad;
    if (wDecay && ad < wTailMin) wTailMin = ad;
    if (wDecay && now - wFireT > W_MIN_GAP && ad > W_RISE_MIN && ad > wTailMin * W_RISE_K){
      wArmed = true; wAcc = 0; wTailMin = Infinity; wDecay = false; wPrevAd = 0;
    } else {
      return;
    }
  }

  wAcc += d;
  if (Math.abs(wAcc) < W_FIRE) return;
  go(wAcc > 0 ? 1 : -1);          /* ★ 量に依らず常に 1 送り ★ */
  wAcc = 0; wArmed = false; wFireT = now;
  wTailMin = Infinity; wDecay = false; wPrevAd = ad;
}, { passive:false });

/* ══ クリック / タップ ═════════════════════════════════════════════════════
   中央 : タップ1 = フリップ（背面）／同じカードへのタップ2 = 商品を開く
   奥   : タップ = そのカードを中央へ呼ぶ（遷移もフリップもしない）
   ドラッグ直後（CLICK_SLOP 超）の click は握り潰す。
   ★ 本番文法（冒頭 (1)）★ ユーザーの原クリックは **常に** preventDefault する。
     遷移させたいときだけ openCard() が同じ 〈a〉 へ実 click を代理発火し、その 1 回だけ
     wantsFlipFirst() が false を返してルーターに拾わせる。 */
var flippedIdx = null;
var navPending = false;    /* 代理発火中のみ true（同期的に立てて同期的に下ろす） */

function setFlip(i, on){
  wraps[i].classList.toggle('is-flipped', !!on);
  var link = wraps[i].querySelector('.card-link');
  if (link) link.setAttribute('aria-pressed', on ? 'true' : 'false');
}
function clearFlip(){ if (flippedIdx !== null){ setFlip(flippedIdx, false); flippedIdx = null; } }
/* 回したらフリップは解ける（v2 の「スクロールでフリップ解除」と同じ考え方）。 */
function clearFlipOnSpin(){ if (flippedIdx !== null && flippedIdx !== curIndex()) clearFlip(); }

/* ★ 商品を開く唯一の関数 ★ ここに実ナビゲーション API を書いてはいけない（§I1/§I3/§R2）。
   〈a class="card-link" href="/products/〈handle〉"〉 への click 代理発火だけを行い、あとは
   snippets/asf-overlay-host.liquid の capture 段委譲に委ねる
   （＝ preventDefault + fetch/inject → history.pushState → sendPageView が 1 回だけ走る）。 */
function openCard(i){
  var link = wraps[i].querySelector('.card-link');
  if (!link) return;
  navPending = true;
  try { link.click(); }
  finally { navPending = false; }
}

/* ══════════════════════════════════════════════════════════════════════════
   wantsFlipFirst(a) — ルーター（capture 段）への回答。契約は grid-2 と同一:
     true  … ルーターは **何もせず素通し**（preventDefault も openFromLink もしない）
     false … ルーターが通常どおり PDP をオーバーレイで開く
   ──────────────────────────────────────────────────────────────────────────
   ★ なぜ必要か ★ ルーターは document の **capture 段** で a[href^="/products/"] のクリックを
     捕まえて PDP を開く。一方この下の host.addEventListener('click', …) は **bubble 段**。
     capture は bubble より先に走るので、素通しにしないとタップ1（フリップ）や奥タップ
     （呼び寄せ）やドラッグ直後の click でも即 PDP が開いてしまう。
   ★ v3 での回答は単純化されている ★ SHOP カード上のクリックは **代理発火の 1 回を除いて
     常に true**（＝ルーターは一切関与しない）。判定の本体は下の bubble ハンドラ 1 箇所だけが
     持つので、capture 側と bubble 側で条件が二重管理になる余地が構造的に無い。
   ★ SHOP 外のリンク（PDP 内の色変種リンク・?product= 等）には関与しない ★ → false。
   ★ 2026-08-03 oneclick 整合確認 ★ PC 中央カードを「クリック1回で openCard()」に変えた後も、
     本関数の契約・実装は 1 行も変えていない。navPending だけで判定しており、bubble 側が
     どの分岐（PC の即時 openCard／SP のフリップ→openCard）を通って openCard() へ辿り着いたかは
     関知しない設計だったため、そのまま整合する。PC の 1 クリックは内部的には
     「原クリック（capture: true で素通し→bubble: preventDefault+openCard）→ 代理クリック
     （capture: navPending=true で false→ルーターがオーバーレイを開く）」という同一クリック内
     2 段ディスパッチを同期的に完走する。ユーザー視点では 1 クリックで PDP が開き、GA
     page_view は代理発火の 1 回のみ（原クリックはルーター未関与のため送出されない）。 */
function wantsFlipFirst(a){
  if (navPending) return false;                     /* 代理発火 = 遷移させる */
  var card = (a && a.closest) ? a.closest('.card-wrap') : null;
  if (!card || !orbit.contains(card)) return false; /* SHOP の軌道カード以外は関与しない */
  return true;
}

host.addEventListener('click', function(e){
  if (navPending) return;                     /* 代理発火の再入（同じ 〈a〉 の click）を無視 */
  var wrap = e.target.closest ? e.target.closest('.card-wrap') : null;
  if (!wrap) return;
  /* ★ 原クリックは常に握り潰す ★ 〈a〉 のネイティブ遷移（＝ページ破棄＝音が止まる）を
     構造的に禁止する。遷移が必要な分岐だけが下で openCard() を呼ぶ。 */
  e.preventDefault();
  abortEntrance();                            /* ★richport★ 演出中でも入力を捨てない */
  if (performance.now() - clickGuard < 260) return;   /* ドラッグ直後の click は握り潰す */
  var i = wraps.indexOf(wrap);
  if (i < 0) return;
  if (i !== curIndex() || tw){          /* 奥のカード（＝回っている最中も奥扱い）= 呼び寄せ（PC/SP不変） */
    clearFlip();
    goToIndex(i);
    return;
  }
  /* ★ 2026-08-03 oneclick ★ クライアント指示「PC版shopの商品詳細ページに行くのに2クリック
     必要なので1クリックで行けるように」への対処。PC(hover:hover かつ pointer:fine) は
     フリップ状態を問わず中央カードのクリック1回で商品詳細（オーバーレイ）を開く。フリップの
     見た目は CSS の #shop .card-wrap.is-center:hover .card__flip（asf-theme-16.css）へ戻した
     ので、ここでの JS フリップ分岐（is-flipped の付け外し・flippedIdx の管理）は SP 専用として
     残す。openCard() は下方 §(1) の代理発火（実 click ディスパッチ）を使うので、
     wantsFlipFirst() の契約は無改変のままこの 1 回のクリックだけでオーバーレイが開く
     （capture 段は 1 回目=素通し／代理発火の 2 回目=false でオーバーレイ、という既存の
     2 段ディスパッチが同一クリック内で同期的に完結するため、ユーザー視点では 1 クリック）。
     HOVER_MQ / SP_MQ は下方で定義されるが var 巻き上げにより本コールバック実行時
     （＝ユーザー操作後）には代入済みなので参照して問題ない。 */
  if (HOVER_MQ.matches && !SP_MQ.matches){
    openCard(i);
    return;
  }
  if (flippedIdx === i){ openCard(i); return; }       /* SP タップ2 = 商品を開く */
  clearFlip();
  setFlip(i, true);
  flippedIdx = i;
});

/* ══ ホバー呼び寄せ（PC のみ）══════════════════════════════════════════════
   ★★ 2026-08-02 pcfix2 — クライアント指示「マウスポインタが T シャツの上に来たら、
      回転すること」★★（本コメントが唯一の記述元）
   中央以外のカードにマウスポインタが乗ったら、そのカードを正面へ呼び寄せる
   （＝既存のクリック呼び寄せ host.click 分岐のホバー版。goToIndex という同じ 1 本の道を通るので、
     着地位置・トゥイーン・逆走ゼロの保証はクリック/キー/フリックとまったく同一）。

   ★ 中央カードのホバーは何もしない ★ クリック1=フリップ / クリック2=PDP の文法は不変。

   ── 暴走させないための 3 つの歯止め（すべて実測でチューニング）──
     ① 意図判定ディレイ HOVER_DELAY(190ms) … 通過しただけでは回さない。
        ポインタが外れた（pointerout）・別カードへ移った時点で即取消。
     ② 回転中（tw !== null）は新しいホバー意図を受け付けない ＝ 連続ホバーで暴れない。
     ③ ★ 最重要 ★ 呼び寄せを 1 回実行したら **実際にポインタが動くまで再武装しない**。
        これが無いと「カードが回ってきてカーソルの下に別のカードが入る → また呼び寄せ →
        永久に回り続ける」という自走ループになる。ブラウザはカーソル静止のままでも
        要素が動けば pointerover を発火するので、pointerover の回数では判定できない。
        clientX/clientY が実際に 3px 超動いたときだけ再武装する（hoverTrack）。
        カーソル静止中に起きる pointerover は座標が同一なので再武装しない＝構造的に止まる。

   ★ タッチ端末では絶対に発動しない ★ (hover:hover) and (pointer:fine) のメディアクエリ・
     SP_MQ・pointerType==='mouse' の 3 重ガード。iPad のトラックパッド等で hover が
     使える環境だけが通る。 */
var HOVER_MQ    = window.matchMedia('(hover:hover) and (pointer:fine)');
var HOVER_DELAY = 190;
var hoverT = null, hoverIdx = -1, hoverArmed = true, hoverX = -1e9, hoverY = -1e9;

function hoverCancel(){
  if (hoverT !== null){ clearTimeout(hoverT); hoverT = null; }
  hoverIdx = -1;
}
/* ポインタが「実際に」動いたときだけ再武装する（上記③）。pointerover の順序は
   boundary → move なので、pointerover 側からも同じ関数を呼ばないと初回入場で取りこぼす。 */
function hoverTrack(e){
  if (Math.abs(e.clientX - hoverX) + Math.abs(e.clientY - hoverY) <= 3) return;
  hoverX = e.clientX; hoverY = e.clientY;
  if (!tw) hoverArmed = true;
}
function hoverEligible(e){
  if (!HOVER_MQ.matches || SP_MQ.matches) return false;
  if (e.pointerType && e.pointerType !== 'mouse') return false;
  return interactive();
}
host.addEventListener('pointermove', function(e){
  if (!hoverEligible(e)) return;
  hoverTrack(e);
}, { passive:true });
host.addEventListener('pointerover', function(e){
  if (!hoverEligible(e)) return;
  hoverTrack(e);
  var wrap = e.target.closest ? e.target.closest('.card-wrap') : null;
  if (!wrap){ hoverCancel(); return; }
  var i = wraps.indexOf(wrap);
  if (i < 0 || i === curIndex() || tw || drag || !hoverArmed){ hoverCancel(); return; }
  if (i === hoverIdx) return;                 /* 同じカード内での再入は無視（子要素間の移動） */
  hoverCancel();
  hoverIdx = i;
  hoverT = setTimeout(function(){
    hoverT = null;
    if (hoverIdx !== i) return;               /* 途中で別カードへ移った */
    if (tw || drag || !hoverArmed) return;
    if (!interactive() || i === curIndex()) return;
    hoverArmed = false;                       /* 実移動があるまで次は撃たない（上記③） */
    abortEntrance();                          /* ★richport★ 演出中のホバーも即整列してから呼び寄せる */
    clearFlip();
    goToIndex(i);
  }, HOVER_DELAY);
}, { passive:true });
host.addEventListener('pointerout', function(e){
  var wrap = e.target.closest ? e.target.closest('.card-wrap') : null;
  if (wrap && wraps.indexOf(wrap) === hoverIdx) hoverCancel();
}, { passive:true });

/* キーボード: ←→ で送る。★本番固有のガード★ document 直結なのでここだけは
   「SHOP がアクティブ かつ PDP オーバーレイが開いていない」ときに限る
   （他タブや PDP でカーソルキーが軌道を回さないように）。 */
document.addEventListener('keydown', function(e){
  if (e.metaKey || e.ctrlKey || e.altKey) return;
  if (!interactive()) return;
  var t = e.target;
  if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
  if (e.key === 'ArrowRight'){ abortEntrance(); clearFlip(); go(1); e.preventDefault(); }
  else if (e.key === 'ArrowLeft'){ abortEntrance(); clearFlip(); go(-1); e.preventDefault(); }
});
/* 奥のカードにフォーカスが入ったらそれを中央へ呼ぶ（Tab だけで一周できる＝視覚に頼らずに
   全バリアントへ到達できる）。 */
host.addEventListener('focusin', function(e){
  if (!interactive()) return;
  var wrap = e.target.closest ? e.target.closest('.card-wrap') : null;
  if (!wrap) return;
  var i = wraps.indexOf(wrap);
  if (i >= 0 && i !== curIndex()){ abortEntrance(); goToIndex(i); }
});

(function(){
  var prev = document.getElementById('shopEdgePrev');
  var next = document.getElementById('shopEdgeNext');
  if (prev) prev.addEventListener('click', function(){ abortEntrance(); clearFlip(); go(-1); });
  if (next) next.addEventListener('click', function(){ abortEntrance(); clearFlip(); go(1); });
})();

/* ══ ヒント ════════════════════════════════════════════════════════════════
   文言はラボ確定版を踏襲（§C5）。Liquid は空の 〈span〉 だけを出し、文言はここで入れる
   （＝HTML ソース上の文字もゼロ）。1 箇所のみ・黒文字（CSS は assets/asf-theme-7.css）。 */
function syncHint(){
  if (!hintEl) return;
  /* ★ 2026-08-02 pcfix2 ★ PC は主たる回転操作がホバー呼び寄せになったので DRAG → HOVER。
     ドラッグもホイールも従来どおり効く（ヒントは 1 箇所・3 節の現行文法を崩さない範囲で
     最も発見してほしい操作を出す）。SP 側の文言は一切変更しない。
     ★ 2026-08-03 oneclick ★ PC は中央カードのフリップがホバー起因（CSS）に戻り、クリックは
     フリップ状態を問わず常に1回で商品詳細へ進むようになったため、案内も2段階
     （FLIP → INFO の順に押す）から「HOVER でフリップ・CLICK で INFO」という並列の1行へ簡略化
     （クライアント指定の文言をそのまま採用）。SP の文言（タップ2段階の案内）は不変。 */
  hintEl.textContent = SP_MQ.matches
    ? 'SWIPE: ROTATE · TAP: FLIP · TAP AGAIN: INFO'
    : 'HOVER: FLIP · CLICK: INFO';
}

/* ══ resize / モード切替 ═══════════════════════════════════════════════════
   iOS の URL バー伸縮（幅は変わらず高さだけ変わる）でも --tee は dvh 由来で変わり得るので、
   幅ガードは掛けずに毎回測り直す（測るのは resize 時だけなのでフレーム内のコストはゼロ）。
   ★ 幅 0（SHOP 非表示）の間は measure() が false を返して何もしない＝
     他ビュー閲覧中の resize でジオメトリが壊れることはない。 */
var rzT = null;
window.addEventListener('resize', function(){
  clearTimeout(rzT);
  rzT = setTimeout(function(){ if (measure()) apply(); }, 90);
}, { passive:true });
if (SP_MQ.addEventListener) SP_MQ.addEventListener('change', function(){ if (measure()) apply(); syncHint(); });
else if (SP_MQ.addListener) SP_MQ.addListener(function(){ if (measure()) apply(); syncHint(); });
if (PRM.addEventListener) PRM.addEventListener('change', function(){ if (tw){ pos = tw.target; tw = null; settle(pos); apply(); } });

/* ══ 起動 / 初回可視化 ═════════════════════════════════════════════════════
   ★本番固有★ 起動時 #shop は .view{display:none} のことが多く（`/` = CLUB 着地）、
   その間 .card-wrap の offsetWidth は 0。measure() は false を返して保留し、SHOP が
   最初に可視化された瞬間に 1 回だけ測って配置する。
   ・MutationObserver … #shop の class（is-active）を監視。ルーターの activate() が
     class を付けた直後（同一タスクのマイクロタスク）に走るので **描画前**に配置が決まる。
   ・IntersectionObserver … display:none の要素は交差しないので、true になった瞬間＝
     「SHOP が実際に画面に出た」と同義。タブ / quicknav / 横フリックのどの経路でも拾える。
   ・hashchange … 上記いずれも非対応の環境向けの保険（冪等）。
   ★ pos はどの経路でもリセットしない ★ ＝ タブ往復で選択中の商品が維持される。
   ★ 初期表示のドックは data 順の先頭（= snippets/asf-product-handles.liquid の CSV 先頭 =
     Ying Yang Block tee / White）★ pos の初期値 0 と apply() の θ=0 がそれを保証する。 */
function ensureLaidOut(){
  if (!measure()) return;
  /* ★ 2026-08-02 richport ★ measure() が通った＝ SHOP に実寸が付いた＝ **初めて画面へ出た瞬間**。
     ラボは DOMContentLoaded を起点にしていたが、本番の既定着地は CLUB（`/`）で SHOP は
     display:none なので、そのままでは「ユーザーが SHOP を開く前に演出が終わっている」ことになる。
     よって登場のゲートはここ（＝ビュー活性化）で 1 度だけ回す。bootEntrance() が
     どの分岐でも apply() を呼ぶので、ここでの重複 apply() は避ける。 */
  /* ★ 2026-08-01 fix2 ★ 「初期表示と同時に画像取得を始める」唯一の正しいタイミング
     （詳細は primeFronts の直上コメント）。★ richport で bootEntrance() より **前** へ移した ★
     登場演出は front の decode 完了を待つので、待ち始める前に fetchPriority を上げておかないと
     演出開始そのものが遅れる。primeBack（中央カードの裏面）は下の apply() 内の is-center 確定で
     従来どおり走る。 */
  primeFronts();
  if (!entBooted){ entBooted = true; bootEntrance(); }
  else apply();
}
syncHint();
ensureLaidOut();
if ('MutationObserver' in window && shopView){
  var mo = new MutationObserver(function(){ if (shopActive()) ensureLaidOut(); });
  mo.observe(shopView, { attributes:true, attributeFilter:['class'] });
}
if ('IntersectionObserver' in window){
  var io = new IntersectionObserver(function(entries){
    for (var i = 0; i < entries.length; i++){
      if (entries[i].isIntersecting){ ensureLaidOut(); break; }
    }
  }, { threshold: 0 });
  io.observe(orbit);
}
window.addEventListener('hashchange', function(){ requestAnimationFrame(ensureLaidOut); });
window.addEventListener('load', function(){ ensureLaidOut(); }, { once:true });

/* ══ 公開 API ══════════════════════════════════════════════════════════════
   ★ 実ナビゲーションを行うメソッドは意図的に 1 つも持たない ★（grid-2 と同じ規律）
   wantsFlipFirst 以外はすべて **読み取り専用の検証フック**（自動回帰・実測用）。
   ★ グローバル名 ★ snippets/asf-overlay-host.liquid の capture 段ハンドラが
     window.ASF_SHOP_GRID.wantsFlipFirst を参照しているため、その名前を維持する
     （旧 assets/asf-shop-grid-2.js は本移植で参照を外したので衝突しない）。
     新しい実体名 window.ASF_SHOP_ORBIT からも同じオブジェクトを引ける。 */
var API = {
  /* snippets/asf-overlay-host.liquid の capture 段ハンドラが参照する（上の定義を参照） */
  wantsFlipFirst: wantsFlipFirst,

  /* --- 以下すべて読み取り専用（検証・実測用） --- */
  isSP: function(){ return SP_MQ.matches; },
  measured: function(){ return measured; },
  pos: function(){ return pos; },
  index: curIndex,
  handle: function(){ return wraps[curIndex()].getAttribute('data-handle'); },
  order: function(){ return wraps.map(function(w){ return w.getAttribute('data-handle'); }); },
  settled: function(){ return tw === null && drag === null; },
  flipped: function(){ return flippedIdx; },
  geo: function(){ return geo; },
  /* いま画面上で各カードがどこに見えているか（右奥=次 / 左奥=前 の機械判定用）。
     getBoundingClientRect は 3D 変換後の実スクリーン矩形を返すので、
     これで「見た目の x 中心・見た目の幅」がそのまま取れる。 */
  layout: function(){
    var ob = orbit.getBoundingClientRect();
    return wraps.map(function(el, i){
      var r = el.getBoundingClientRect();
      var m = shown[i] || {};
      return { i:i, handle:el.getAttribute('data-handle'),
               deg: Math.round(m.deg || 0),
               z: -Math.round(m.depth || 0), s: +(m.s || 1).toFixed(3),
               rot: +(m.rot || 0).toFixed(1),
               cx: +((r.left + r.width / 2) - (ob.left + ob.width / 2)).toFixed(1),
               cy: +((r.top + r.height / 2) - (ob.top + ob.height / 2)).toFixed(1),
               w: +r.width.toFixed(1), h: +r.height.toFixed(1),
               L: +r.left.toFixed(1), R: +r.right.toFixed(1),
               T: +r.top.toFixed(1), B: +r.bottom.toFixed(1),
               opacity: +(el.style.filter.match(/opacity\(([\d.]+)\)/) || [0,1])[1] };
    });
  },
  /* 直近フリックの実測: vpx=離指速度(px/s) / n=速度バンドが返した送り数 /
     steps=クランプ後に実際に送った数 / dir=向き。しきい値の検証専用。 */
  lastFlick: function(){ return lastFlick; },
  flickSteps: function(vpx){ return flickSteps(vpx / geo.pxPerStep); },
  /* 逆走ゼロの機械判定用フレームトレース（読み取り専用） */
  traceStart: function(){ trace = []; },
  traceStop: function(){ var t = trace; trace = null; return t; }
};
window.ASF_SHOP_ORBIT = API;
window.ASF_SHOP_GRID  = API;
});
